import React, { useState, useRef, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  IconButton,
  Stack,
  Paper,
  keyframes,
} from "@mui/material";
import {
  Mic as MicIcon,
  Stop as StopIcon,
  Delete as DeleteIcon,
  CloudUpload as UploadIcon,
  Pause as PauseIcon,
  PlayArrow as PlayIcon,
} from "@mui/icons-material";

const pulse = keyframes`
  0% { transform: scale(1); opacity: 1; }
  50% { transform: scale(1.1); opacity: 0.7; }
  100% { transform: scale(1); opacity: 1; }
`;

function RecordAudioDialog({ open, onClose, onUpload }) {
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const timerIntervalRef = useRef(null);

  const [recording, setRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [recordings, setRecordings] = useState([]);

  // Format seconds to MM:SS
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  // Timer logic
  useEffect(() => {
    if (recording && !isPaused) {
      timerIntervalRef.current = setInterval(() => {
        setElapsedTime((prev) => prev + 1);
      }, 1000);
    } else {
      clearInterval(timerIntervalRef.current);
    }
    return () => clearInterval(timerIntervalRef.current);
  }, [recording, isPaused]);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;

      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      chunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) chunksRef.current.push(event.data);
      };

    mediaRecorder.onstop = () => {
  const blob = new Blob(chunksRef.current, { type: "audio/wav" });
  const url = URL.createObjectURL(blob);

  setRecordings((prev) => [
    {
      id: Date.now(),
      blob,
      url,
      name: `Recording ${prev.length + 1}`,
      duration: formatTime(elapsedTime),
    },
    ...prev,
  ]);

  stream.getTracks().forEach((track) => track.stop());
  setElapsedTime(0);
};


      mediaRecorder.start();
      setRecording(true);
      setIsPaused(false);
    } catch (err) {
      console.error("Microphone error:", err);
      alert("Microphone access denied.");
    }
  };

  const pauseRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === "recording") {
      mediaRecorderRef.current.pause();
      setIsPaused(true);
    }
  };

  const resumeRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === "paused") {
      mediaRecorderRef.current.resume();
      setIsPaused(false);
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
      mediaRecorderRef.current.stop();
      setRecording(false);
      setIsPaused(false);
    }
  };

  const handleUploadClick = (rec) => {
    if (onUpload) onUpload(rec.blob, rec.name, rec.duration);
  };

  const deleteStoredRecording = (id) => {
    setRecordings((prev) => prev.filter((rec) => rec.id !== id));
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: "16px" } }}>
      <DialogTitle sx={{ fontWeight: 700, textAlign: "center" }}>Record Your Audio</DialogTitle>

      <DialogContent>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            py: 4,
            backgroundColor: "#f8f9fa",
            borderRadius: "16px",
            border: "2px dashed #e0e0e0",
            mb: 4,
          }}
        >
          {/* Timer Display */}
          <Typography variant="h3" sx={{ mb: 2, fontWeight: 700, fontFamily: "monospace", color: recording ? "#ff4d4d" : "text.secondary" }}>
            {formatTime(elapsedTime)}
          </Typography>

          <Box sx={{ display: "flex", alignItems: "center", gap: 3 }}>
            {/* Conditional Pause/Resume Button */}
            {recording && (
              <IconButton 
                onClick={isPaused ? resumeRecording : pauseRecording}
                sx={{ backgroundColor: "#0062ff", color: "white", "&:hover": { backgroundColor: "#2d56bd" } }}
              >
                {isPaused ? <PlayIcon fontSize="large" /> : <PauseIcon fontSize="large" />}
              </IconButton>
            )}

            {/* Main Record/Stop Button */}
            <Box sx={{ position: "relative" }}>
              {recording && !isPaused && (
                <Box
                  sx={{
                    position: "absolute",
                    top: -10, left: -10, right: -10, bottom: -10,
                    borderRadius: "50%",
                    border: "3px solid #ff4d4d",
                    animation: `${pulse} 1.6s infinite ease-in-out`,
                  }}
                />
              )}
              <IconButton
                onClick={recording ? stopRecording : startRecording}
                sx={{
                  width: 80, height: 80,
                  backgroundColor: recording ? "#ff4d4d" : "#3b66ff",
                  color: "white",
                  "&:hover": { backgroundColor: recording ? "#d32f2f" : "#2a55e0" },
                }}
              >
                {recording ? <StopIcon sx={{ fontSize: 40 }} /> : <MicIcon sx={{ fontSize: 40 }} />}
              </IconButton>
            </Box>
          </Box>

          <Typography variant="subtitle1" fontWeight={600} sx={{ mt: 2 }}>
            {recording ? (isPaused ? "Paused" : "Recording...") : "Ready to record"}
          </Typography>
        </Box>

        {/* List of Recordings (Same as your original) */}
        {recordings.length > 0 && (
          <Box sx={{ mt: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 700, color: "#555" }}>
              Recent Recordings
            </Typography>
            <Stack spacing={2}>
              {recordings.map((rec) => (
                <Paper key={rec.id} elevation={0} sx={{ p: 2, borderRadius: "12px", border: "1px solid #e0e0e0" }}>
                  <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                    <Typography variant="subtitle2">{rec.name} </Typography>
                    <IconButton size="small" onClick={() => deleteStoredRecording(rec.id)} color="error">
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                  <Box sx={{ display: "flex", gap: 2 }}>
                    <audio controls src={rec.url} style={{ height: "35px", flexGrow: 1 }} />
                    <Button variant="contained" size="small" startIcon={<UploadIcon />} onClick={() => handleUploadClick(rec)}>
                      Upload
                    </Button>
                  </Box>
                </Paper>
              ))}
            </Stack>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button onClick={onClose} color="inherit">Dismiss</Button>
      </DialogActions>
    </Dialog>
  );
}

export default RecordAudioDialog;